import { isPremium } from '@/utils/kuraemon_plans'

/**
 * メンバー一覧を参加時刻(joined_at)降順にソートし、指定されたアカウントを先頭に設定する.
 * @param {{account_id: string, joined_at: number }[]} accountInfoList 工事参加アカウントリスト
 * @param {string} account_id アカウントID
 * @returns ソート後のメンバー一覧
 */
 const sortedMemberList = (accountInfoList, account_id) => {

  // 参加時刻(joined_at)降順ソート
  const members = [].concat(accountInfoList);
  members.sort((a, b) => (b.joined_at - a.joined_at))

  // アカウントIDがない場合
  if(!account_id) return members

  // 指定されたアカウントを先頭に設定する.
  const curMember = members.filter(member => account_id === member.account_id)
  const otherMembers = members.filter(member => account_id !== member.account_id)
  return curMember.concat(otherMembers)
};

const set = property => (state, payload) => (state[property] = payload)
const get = property => (state) => (state[property])

const defaultStateProc = () => ({
  temporaryAccountInfo: null,
  accountInfo: null,
  talkNotificationUrls: {},
  accountDetailInfo: {},
  accountLicenseInfo: null,
  registeredAccountInfo: null,
  // APIでチェックしたトークンの状態をためておく. キー:トークン、値:true->OK, false->NG
  activeOnetimeTokenList: {},
  kokubanList: null,
  cloudConstructionInfo: null,
  loginAppInfoList: [],
  joinRequestList: null,
  cloudConstructionQrCode: null,
  zumens: null,
  zumenEntities: null,
  zumenPages: null,
  zumenInfo: null,
  appInfoList: null,
  participatingConstruction: null,
  bookrackInfo: null,
  amnimoCameraList: [],
  amnimoTokenInfo: null,
  amnimoHls: null,
  amnimoSnapShot: null,
  kuraemonLiveList: [],
  kuraemonLiveHlsUrls: {},
  kuraemonLiveThumbnails:[],
  kuraemonLiveHistory: [],
  kuraemonLiveRecodingCalender:[],
})

export default {
  namespaced: true,
  state: defaultStateProc(),
  getters: {
    temporaryAccountInfo: get('temporaryAccountInfo'),
    accountInfo: get('accountInfo'),
    talkNotificationUrls: get('talkNotificationUrls'),
    accountDetailInfo: get('accountDetailInfo'),
    accountLicenseInfo: get('accountLicenseInfo'),
    displayPlan: (state) => {
      if(!state.accountLicenseInfo) return {};
      if(state.accountLicenseInfo.next_account_service_contract_info){
        if(isPremium(state.accountLicenseInfo.next_account_service_contract_info.service_contract_type)){
          // 次のプラン情報がpremiumの場合のみ、それを優先して表示する
          return state.accountLicenseInfo.next_account_service_contract_info
        }
      }
      // それ以外は現在のプランを表示
      return state.accountLicenseInfo.account_service_contract_info;
    },
    registeredAccountInfo: get('registeredAccountInfo'),
    activeOnetimeToken: (state) => (onetimeToken) => ( !!state.activeOnetimeTokenList[onetimeToken] ),
    cloudConstructionInfo: get('cloudConstructionInfo'),
    loginAppInfoList: get('loginAppInfoList'),
    joinRequestList: get('joinRequestList'),
    cloudConstructionQrCode: get('cloudConstructionQrCode'),
    zumens: get('zumens'),
    zumenEntities: get('zumenEntities'),
    zumenPages: get('zumenPages'),
    zumenInfo: get('zumenInfo'),
    appInfoList: get('appInfoList'),
    participatingConstruction: get('participatingConstruction'),
    constructionMembers: (state) => (account_info_list) => {
      const account_id = state.accountInfo
        ? state.accountInfo.account_id
        : null
        ;
      if(account_info_list) return sortedMemberList(account_info_list, account_id)
      if(!state.cloudConstructionInfo) return []
      return sortedMemberList(state.cloudConstructionInfo.account_info_list, account_id)
    },
    bookrackInfo: get('bookrackInfo'),
    amnimoCameraList: get('amnimoCameraList'),
    amnimoTokenInfo: get('amnimoTokenInfo'),
    amnimoHls: get('amnimoHls'),
    amnimoSnapShot: get('amnimoSnapShot'),
    kuraemonLiveList: get('kuraemonLiveList'),
    kuraemonLiveHlsUrls: get('kuraemonLiveHlsUrls'),
    kuraemonLiveThumbnails: get('kuraemonLiveThumbnails'),
    kuraemonLiveHistory: get('kuraemonLiveHistory'),
    kuraemonLiveRecodingCalender: get('kuraemonLiveRecodingCalender'),
  },
  mutations: {
    init(state) {
      Object.assign(state, defaultStateProc())
    },
    setTemporaryAccountInfo: set('temporaryAccountInfo'),
    setAccountInfo: set('accountInfo'),
    setTalkNotificationUrls: set('talkNotificationUrls'),
    // 現在のログインアカウントの情報を更新
    updateCurrentAccountInfo: (state, updateAccountDetailInfo) => {
      const currentAccountInfo = state.accountInfo;
      for(const k of Object.keys(currentAccountInfo)) {
        if(!Object.prototype.hasOwnProperty.call(updateAccountDetailInfo, k)) continue;
        currentAccountInfo[k] = updateAccountDetailInfo[k]
      }
      state.accountInfo = currentAccountInfo;
    },
    setAccountDetailInfo: set('accountDetailInfo'),
    // 編集中のアカウント情報をマージ
    mergeAccountDetailInfo: (state, updateAccountDetailInfo) => {
      const mergedAccountDetailInfo = {
        ...state.accountDetailInfo,
        ...updateAccountDetailInfo,
      }
      state.accountDetailInfo = mergedAccountDetailInfo;
    },
    setAccountLicenseInfo: set('accountLicenseInfo'),
    setRegisteredAccountInfo: set('registeredAccountInfo'),
    setActiveOnetimeToken(state, { onetimeToken, active }) {
      state.activeOnetimeTokenList[onetimeToken] = active
      state.activeOnetimeTokenList = Object.assign({}, state.activeOnetimeTokenList, state.activeOnetimeTokenList)
    },
    setCloudConstructionInfo: set('cloudConstructionInfo'),
    setLoginAppInfoList: set('loginAppInfoList'),
    setJoinRequestList: set('joinRequestList'),
    setCloudConstructionQrCode: set('cloudConstructionQrCode'),
    setZumens: set('zumens'),
    setZumenEntities: set('zumenEntities'),
    setZumenPages: set('zumenPages'),
    setZumenInfo: set('zumenInfo'),
    setAppInfoList: set('appInfoList'),
    setParticipatingConstruction: set('participatingConstruction'),
    setBookrackInfo: set('bookrackInfo'),
    setAmnimoCameraList: set('amnimoCameraList'),
    setAmnimoTokenInfo: set('amnimoTokenInfo'),
    setAmnimoHls: set('amnimoHls'),
    setAmnimoSnapShot: set('amnimoSnapShot'),
    setKuraemonLiveList: set('kuraemonLiveList'),
    setKuraemonLiveHlsUrls: set('kuraemonLiveHlsUrls'),
    setKuraemonLiveThumbnails: set('kuraemonLiveThumbnails'),
    setKuraemonLiveHistory: set('kuraemonLiveHistory'),
    setKuraemonLiveRecodingCalender: set('kuraemonLiveRecodingCalender'),
  },
  actions: {
    clearParticipatingConstruction(context) {
      context.commit("setParticipatingConstruction", null)
    },
    isCurrentConstructionManager(context){
      // 現在の工事
      const cloudConstructionInfo = context.getters['cloudConstructionInfo'];
      if(!cloudConstructionInfo) return false;

      // ログインユーザー
      const accountInfo = context.getters['accountInfo'];
      if(!accountInfo) return false;

      // 工事メンバー
      const members = cloudConstructionInfo.account_info_list;
      // ログインユーザーの工事メンバー情報
      const memberInfo = members.find(member => member.account_id === accountInfo.account_id);

      if(!memberInfo) return false;

      // 管理者かどうかを返す
      return memberInfo.role_type === 'manager'
    },
  },
}
